import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink, Route } from 'react-router-dom';
import { Link, Breadcrumbs } from '@material-ui/core';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
    indented: {
        paddingLeft: theme.spacing(8),
    },
    link: {
        position: 'relative',
    },
    icon: {
        position: 'relative',
        top: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        '& svg': {
            height: 20,
        },
    },
}));

export interface BreadcrumbItem {
    label: string;
    path: string;
    icon?: React.ReactNode;
}

interface CommonBreadcrumbProps {
    indented?: boolean;
    'aria-label'?: string;
    excludeLast?: boolean;
    separator?: React.ReactNode;
}

export interface BreadcrumbProps extends CommonBreadcrumbProps {
    items: BreadcrumbItem[];
}

export const Breadcrumb: React.FunctionComponent<BreadcrumbProps> = props => {
    const classes = useStyles();
    return (
        <Breadcrumbs
            className={clsx(props.indented && classes.indented)}
            aria-label={props['aria-label'] || 'breadcrumb'}
            separator={props.separator}
        >
            {props.items.map(({ label, path, icon }, i, a) => (
                <Link
                    key={i}
                    component={RouterLink}
                    to={path}
                    className={classes.link}
                    color={i < a.length || props.excludeLast ? 'inherit' : 'textPrimary'}
                >
                    {icon && <span className={classes.icon}>{icon}</span>}
                    {label}
                </Link>
            ))}
        </Breadcrumbs>
    );
};

export interface RouterBreadcrumbProps extends CommonBreadcrumbProps {
    routePartMapping: {
        [routePart: string]: string|{label: string, icon: React.ReactNode}
    };
}

export const RouterBreadcrumb: React.FunctionComponent<RouterBreadcrumbProps> = props => {
    const { routePartMapping, excludeLast, ...routerProps } = props;
    return (
        <Route>
            {({ location }) => {
                const pathnames = location.pathname.split('/').filter(x => x);
                const parts = pathnames.reduce(([ path, items ], part) => {
                    const to = path + '/' + part;
                    const mapping = routePartMapping[to];
                    const label = mapping ? mapping.label ? mapping.label : mapping : part;
                    const icon = mapping && mapping.icon ? mapping.icon : null;
                    return [
                        to,
                        [ ...items, { path: to, label, icon } ],
                    ];
                }, [ '', [] ]);
                return <Breadcrumb items={excludeLast ? parts[1].slice(0, -1) : parts[1]} {...routerProps} />;
            }}
        </Route>
    );
};
